import Vue from 'vue'
import Vuex from 'vuex'
import router from './router'
import { getDefaultLoggedInRoute, NAMES as ROUTE_NAMES } from '@/routes'

import plugins from 'store/plugins'

import Yggdrasil from 'services/yggdrasil'
import i18n from 'services/i18n'
import config from 'services/config'

// Modules
import certificates from 'store/modules/certificates'
import chatBot from 'store/modules/chatBot'
import completedCourses from 'store/modules/completedCourses'
import courseFilter from 'store/modules/courseFilter'
import courses from 'store/modules/courses'
import coursesCollections from 'store/modules/coursesCollections'
import languages from 'store/modules/languages'
import learningPaths from 'store/modules/learningPaths'
import loaderScreen from 'store/modules/loaderScreen'
import menu from 'store/modules/menu'
import microLearning from 'store/modules/microLearning'
import notes from 'store/modules/notes'
import reports from 'store/modules/reports'
import ratings from 'store/modules/ratings'
import reportsCourses from 'store/modules/reportsCourses'
import reportsUsers from 'store/modules/reportsUsers'
import restricted from 'store/modules/restricted'
import search from 'store/modules/search'
import user from 'store/modules/user'
import videoLog from 'store/modules/videoLog'
import videoPlayer from 'store/modules/videoPlayer'
import watchlists from 'store/modules/watchlists'
import hubspot from './store/modules/hubspot'
import chatBotCombo from './store/modules/chatBotCombo'
import breadcrumb from './store/modules/breadcrumb'
import quiz from './store/modules/quiz'

function fetchFromStorage (storage, key, defaultValue) {
    let fetchedValue = null
    switch (storage) {
    case 'session':
        fetchedValue = window.sessionStorage.getItem(key)
        break
    case 'local':
        fetchedValue = window.localStorage.getItem(key)
        break
    }
    try {
        return JSON.parse(fetchedValue) ?? defaultValue
    } catch (e) {
        return defaultValue
    }
}

export function fetchFromLocalStorage (key, defaultValue) {
    return fetchFromStorage('local', key, defaultValue)
}

export function fetchFromSessionStorage (key, defaultValue) {
    return fetchFromStorage('session', key, defaultValue)
}

const ssoJoinDataStorageKey = 'ssoJoinData'
const defaultSsoJoinData = {
    joinToken: null,
    ssoUserData: { firstName: '-', lastName: '-', email: '-' },
}

const ssoJoinData = fetchFromLocalStorage(ssoJoinDataStorageKey, defaultSsoJoinData)

const state = {
    messages: [], // stores internal messages
    userId: fetchFromLocalStorage('userId', 0),
    agreementSigned: false,
    liveWatchedTimes: fetchFromLocalStorage('liveWatchedTimes', { lives: {} }),
    language: config.appLanguage,
    notification: fetchFromLocalStorage('notification', null),
    notificationVisible: fetchFromLocalStorage('notificationVisible', true),
    notificationHeight: 0,
    isTotemStarted: false,
    isSidebarCollapsed: JSON.parse(window.localStorage.getItem('isSidebarCollapsed') || true),
    isSearchMobileCollapsed: fetchFromLocalStorage('isSearchMobileCollapsed', true),
    isListViewVertical: fetchFromLocalStorage('isListViewVertical', false),
    joinToken: ssoJoinData.joinToken,
    ssoUserData: ssoJoinData.ssoUserData,
    ssoAuthorizationCode: null,
    sharedWatchlistModalShown: fetchFromLocalStorage('sharedWatchlistModalShown', false),
    previousPathBeforeLogin: fetchFromSessionStorage('previousPathBeforeLogin', null),
    isMaintenanceOn: false,
    courseListScrollPosition: null,
    previousRoutesCount: fetchFromSessionStorage('previousRoutesCount', 0),
    alertMessage: null
}

const mutations = {
    PUSH_MESSAGE (state, msg) {
        state.messages.push(msg)
    },
    START_TOTEM (state) {
        state.isTotemStarted = true
    },
    LOGOUT (state) {
        state.userId = 0
    },
    LOGIN (state, { userId }) {
        state.userId = userId
    },
    SET_SSO_AUTHORIZATION_CODE (state, payload) {
        if (!payload) {
            state.ssoAuthorizationCode = null
            return
        }
        state.ssoAuthorizationCode = {
            code: payload.code,
            providerId: payload.providerId
        }
    },
    SET_SSO_JOIN_DATA (state, { joinToken, ssoUserData }) {
        window.localStorage.setItem('ssoJoinData', JSON.stringify({
            joinToken: joinToken,
            ssoUserData: ssoUserData
        }))
        state.joinToken = joinToken
        state.ssoUserData = ssoUserData
    },
    SET_SIGNED_AGREEMENT (state, signed) {
        state.agreementSigned = signed
    },
    SET_NOTIFICATION (state, notification) {
        state.notification = notification
    },
    TOGGLE_NOTIFICATION_VISIBILITY (state, visible) {
        state.notificationVisible = visible
    },
    SET_NOTIFICATION_HEIGHT (state, height) {
        state.notificationHeight = height
    },
    INCREMENT_WATCHED_LIVE_TIME (state, id) {
        if (!state.liveWatchedTimes.lives[id]) {
            state.liveWatchedTimes.lives[id] = 3
        } else {
            state.liveWatchedTimes.lives[id] += 3
        }
    },
    TOGGLE_SIDEBAR (state) {
        state.isSidebarCollapsed = !state.isSidebarCollapsed
    },
    TOGGLE_SEARCH_MOBILE (state) {
        state.isSearchMobileCollapsed = !state.isSearchMobileCollapsed
    },
    ASSIGN_LIST_VIEW_TOGGLE_STATE (state, value) {
        state.isListViewVertical = value
    },
    SET_SHARE_WATCHLIST_MODAL_SHOWN (state, value) {
        state.sharedWatchlistModalShown = value
    },
    SET_PREVIOUS_PATH_BEFORE_LOGIN (state, path) {
        state.previousPathBeforeLogin = path
    },
    SET_MAINTENANCE_STATE (state, isMaintenanceOn) {
        state.isMaintenanceOn = isMaintenanceOn
    },
    SET_SCROLL_POSITION (state, position) {
        state.courseListScrollPosition = position
    },
    SET_PREVIOUS_ROUTES_COUNT (state, count) {
        window.sessionStorage.setItem('previousRoutesCount', count)
    },
    SET_ALERT_MESSAGE (state, message) {
        state.alertMessage = message
    }
}

async function promiseLoader (store, loaderTaskKey, fn) {
    try {
        await store.dispatch('LoaderScreen/addTaskToLoader', { key: loaderTaskKey })
        const data = await fn()
        await store.dispatch('LoaderScreen/removeTaskFromLoader', loaderTaskKey)
        return data
    } catch (e) {
        store.dispatch('LoaderScreen/removeTaskFromLoader', loaderTaskKey)
        throw e
    }
}

async function loadInitialDataForLoggedUser (store) {
    store.dispatch('User/setDataLoaded', false)
    await store.dispatch('validateSession')
    store.dispatch('User/loadPackageInfo')
    store.dispatch('Menu/loadCategories').then(() => {
        store.dispatch('Menu/createStaticLinks')
        store.dispatch('Menu/createMenuCategories')
        store.dispatch('Menu/createMenuLanguages')
        store.dispatch('Menu/createMenuLogged')
    })
    store.dispatch('Search/setHideDemoContentToggle', true)
    if (config.appLanguage !== 'dk') {
        store.dispatch('LearningPaths/loadSubscribedLearningPaths')
    }
    store.dispatch('Search/setDefaultSortValue')
    store.dispatch('User/setDataLoaded', true)
    store.dispatch('Courses/loadLspSupportedFormats')
    store.dispatch('Courses/loadLspSupportedLanguages')
    if (store.rootGetters['User/getIsReportingAllowed']) {
        await store.dispatch('Reports/loadReportingConfiguration')
    }
}

async function loadInitialDataForNotLoggedUser (store) {
    store.dispatch('User/loadPackageInfo')
    store.dispatch('Menu/loadCategories').then(() => {
        store.dispatch('Menu/createStaticLinks')
        store.dispatch('Menu/createMenuCategories')
        store.dispatch('Menu/createMenuLanguages')
        store.dispatch('Menu/createMenuNonLogged')
        store.dispatch('Courses/loadAllDigitalClassRoomEvents')
    })
    store.dispatch('Search/setDefaultSortValue')
    store.dispatch('User/setDataLoaded', true)
}

async function clearSessionData (store) {
    await Promise.all([
        store.dispatch('User/setDataLoaded', false),
        store.dispatch('User/clearUserData'),
        store.dispatch('CompletedCourses/clearAfterLogout'),
        store.dispatch('VideoLog/clearAfterLogout'),
        store.dispatch('Notes/clearAfterLogout'),
        store.dispatch('Watchlists/clearAfterLogout'),
        store.dispatch('VideoPlayer/clearVideoPlayer'),
        store.dispatch('Restricted/clearAfterLogout'),
        store.dispatch('Search/setHideDemoContentToggle', false),
        store.dispatch('ReportsUsers/clearAfterLogout'),
        store.dispatch('ReportsCourses/clearAfterLogout'),
        store.dispatch('Reports/clearAfterLogout'),
        store.dispatch('ChatBot/setShowChatBot', false),
        store.dispatch('Courses/clearAfterLogout'),
        store.dispatch('Hubspot/ClearVisitorIdentificationToken'),
    ])
    document.cookie = '__hstc=;path=/;'
    document.cookie = '__hssrc=;path=/;'
    document.cookie = '__hssc=;path=/;'
    document.cookie = 'hubspotutk=;  path=/;'
    if (window.HubSpotConversations && window.HubSpotConversations.widget) {
        window.HubSpotConversations.widget.remove()
        window.HubSpotConversations.clear({ resetWidget: true })
    }
    localStorage.removeItem('dialogueShown')
    store.commit('LOGOUT')
    store.commit('SET_SIGNED_AGREEMENT', false)
}

async function saveVideoLogs (store) {
    const localStorageItems = { ...localStorage }
    const videoLogs = Object.keys(localStorageItems).filter(item => item.startsWith('videoLogs' + state.userId))
    videoLogs.forEach((videoLog) => store.dispatch('VideoPlayer/sendVideoLogToAesirOnStart', videoLog))
    videoLogs.forEach((videoLog) => window.localStorage.removeItem(videoLog))
}

async function doLogin (store, loginCallback) {
    const onLogin = async () => {
        const { id, userData, tokenExpirationTime, joinToken } = await loginCallback()
        if (joinToken !== undefined) {
            store.commit('SET_SSO_JOIN_DATA', {
                joinToken: joinToken,
                ssoUserData: userData,
            })

            await router.push({ name: ROUTE_NAMES.SSO_JOIN })
            return
        }
        store.commit('LOGIN', { userId: id })
        store.dispatch('ChatBot/setChatTokenExpiration', tokenExpirationTime)
        store.dispatch('User/setLoginBox', false)
        store.dispatch('Languages/setSelectedLanguage', store.rootGetters['Languages/getSelectedCountryCode'], { root: true })
        await store.dispatch('User/setCurrentUser', userData)
        await loadInitialDataForLoggedUser(store)
        store.commit('SET_SSO_AUTHORIZATION_CODE', null)

        if (store.state.previousPathBeforeLogin) {
            router.replace(store.state.previousPathBeforeLogin)
            store.dispatch('setPreviousPathBeforeLogin', null)
        } else {
            router.push(getDefaultLoggedInRoute())
        }
    }
    try {
        await promiseLoader(store, 'login', onLogin)
    } catch (e) {
        console.log('Login error', e)
        if (e.code === 'ECONNABORTED' || e.response.status === 'ECONNABORTED') {
            router.push({ name: 'page-something-went-wrong' })
        }
        if (e.code === 1112 || e.response.status === 1112) {
            throw e
        }
        throw i18n.t('General.bad_credentials')
    }
}

const actions = {
    async saveVideoLogsOnInitialize (store) {
        await saveVideoLogs(store)
    },
    setMessage (store, msg) {
        store.commit('PUSH_MESSAGE', msg)
    },
    async startTotem (store, isSSOLogin) {
        const loaderTaskKey = 'startingItems'
        const onStartTotem = async () => {
            if (!store.getters.isLoggedIn && !isSSOLogin) {
                await loadInitialDataForNotLoggedUser(store)
            } else if (store.getters.isLoggedIn) {
                await loadInitialDataForLoggedUser(store)
                await store.dispatch('loadUserData')
            }
            store.dispatch('getNotification', 1)
            store.commit('START_TOTEM')
        }

        return promiseLoader(store, loaderTaskKey, onStartTotem)
    },
    async validateSession (store) {
        try {
            if (store.getters.isLoggedIn) {
                await store.dispatch('User/loadUserInfo')
            }
        } catch (e) {
            await clearSessionData(store)
        }
    },
    async loadUserData (store) {
        const loaderTaskKey = 'loadUserData'
        store.dispatch('LoaderScreen/addTaskToLoader', { key: loaderTaskKey })
        const onLoadUserData = async () => {
            Promise.all([
                store.dispatch('Notes/loadNotes'),
                store.dispatch('CompletedCourses/loadCertificates'),
                store.dispatch('VideoLog/loadCoursesForVideolog'),
                store.dispatch('Watchlists/loadWatchlists'),
                store.dispatch('Watchlists/loadSubordinates'),
                store.dispatch('Ratings/loadUserRatings'),
                store.dispatch('Courses/loadAllDigitalClassRoomEvents'),
                store.dispatch('Courses/loadSignedUpEvents')
            ])
        }

        return promiseLoader(store, loaderTaskKey, onLoadUserData)
    },
    async login (store, { email, password }) {
        await doLogin(store, async () => Yggdrasil.login(email, password))
    },
    async loginWithSSOAuthorizationCode (store, { code, providerId }) {
        store.commit('SET_SSO_AUTHORIZATION_CODE', { code, providerId })
        await doLogin(store, async () => Yggdrasil.loginWithSSOAuthorizationCode(code, providerId))
    },
    async requestSSOLoginUrl (store, serviceId) {
        return Yggdrasil.requestSSOLoginUrl(serviceId)
    },
    async clearSession (store) {
        const loaderTaskKey = 'clearing-session'
        return promiseLoader(store, loaderTaskKey, async () => {
            await clearSessionData(store)
            return loadInitialDataForNotLoggedUser(store)
        })
    },
    async logout (store) {
        const loaderTaskKey = 'logout'
        return promiseLoader(store, loaderTaskKey, async () => {
            await Yggdrasil.logout()
            await clearSessionData(store)
            return loadInitialDataForNotLoggedUser(store)
        })
    },
    async changePassword (store, { password, token }) {
        const handler = async () => Yggdrasil.changePassword(password, token)
        return promiseLoader(store, 'change-password', handler)
    },
    async changePasswordRequest (store, email) {
        const handler = async () => Yggdrasil.changePasswordRequest(email)
        return promiseLoader(store, 'change-password', handler)
    },
    setSignedAgreement (store, signed) {
        store.commit('SET_SIGNED_AGREEMENT', signed)
    },
    async getNotification (store, context) {
        const existingNotification = store.getters.getCurrentNotification
        try {
            let notification = await Yggdrasil.loadNotification(context)
            store.commit('SET_NOTIFICATION', notification.description)
            if (!notification || !notification.description) {
                store.commit('TOGGLE_NOTIFICATION_VISIBILITY', false)
            } else if (notification.description !== existingNotification) {
                store.commit('TOGGLE_NOTIFICATION_VISIBILITY', true)
            }
        } catch (e) {
        }
    },
    hideNotification (store) {
        store.commit('TOGGLE_NOTIFICATION_VISIBILITY', false)
        store.commit('SET_NOTIFICATION_HEIGHT', 0)
    },
    setNotificationHeight (store, height) {
        store.commit('SET_NOTIFICATION_HEIGHT', height)
    },
    progressLive (store, id) {
        store.commit('INCREMENT_WATCHED_LIVE_TIME', id)
    },
    toggleSidebar (store) {
        store.commit('TOGGLE_SIDEBAR')
    },
    toggleSearchMobile (store) {
        store.commit('TOGGLE_SEARCH_MOBILE')
    },
    toggleListView (store, value) {
        store.commit('ASSIGN_LIST_VIEW_TOGGLE_STATE', value)
    },
    clearSsoJoinData (store) {
        store.commit('SET_SSO_JOIN_DATA', defaultSsoJoinData)
        window.localStorage.removeItem(ssoJoinDataStorageKey)
    },
    clearSsoAuthorizationCode (store) {
        store.commit('SET_SSO_AUTHORIZATION_CODE', null)
    },
    markSharedWatchlistModalAsShown (store) {
        store.commit('SET_SHARE_WATCHLIST_MODAL_SHOWN', true)
    },
    setPreviousPathBeforeLogin (store, path) {
        store.commit('SET_PREVIOUS_PATH_BEFORE_LOGIN', path)
    },
    checkResetPasswordTokenValidity (store, token) {
        return Yggdrasil.checkResetPasswordTokenValidity(token)
    },
    setMaintenance (store, isMaintenanceOn) {
        store.commit('SET_MAINTENANCE_STATE', isMaintenanceOn)
    },
    setScrollPosition (store, position) {
        store.commit('SET_SCROLL_POSITION', position)
    },
    setPreviousRoutesCount (store, count) {
        store.commit('SET_PREVIOUS_ROUTES_COUNT', count)
    },
    setAlertMessage (store, message) {
        store.commit('SET_ALERT_MESSAGE', message)
    },
}

const getters = {
    isTotemStarted: (state) => state.isTotemStarted,
    isLoggedIn: (state) => !!state.userId,
    getLanguage: (state) => state.language,
    getCurrentNotification: (state) => state.notification,
    isNotificationVisible: (state) => state.notificationVisible,
    getNotificationHeight: (state) => state.notificationHeight,
    getLiveWatchedTime: (state) => (id) => state.liveWatchedTimes.lives[id],
    getIsSidebarCollapsed: (state) => state.isSidebarCollapsed,
    getIsSearchMobileCollapsed: (state) => state.isSearchMobileCollapsed,
    getListViewToggleState: (state) => state.isListViewVertical,
    getJoinToken: state => state.joinToken,
    getSSOUserData: state => state.ssoUserData,
    getSSOAuthorizationCode: state => state.ssoAuthorizationCode,
    getUserId: state => state.userId,
    getPreviousPathBeforeLogin: state => state.previousPathBeforeLogin,
    getSharedWatchlistModalShown: state => state.sharedWatchlistModalShown,
    getMaintenanceState: state => state.isMaintenanceOn,
    getScrollPositionForCourseList: state => state.courseListScrollPosition,
    getAlertMessage: state => state.alertMessage,
}

Vue.use(Vuex)
const modules = {
    Certificates: certificates,
    ChatBot: chatBot,
    CompletedCourses: completedCourses,
    CourseFilter: courseFilter,
    Courses: courses,
    CoursesCollections: coursesCollections,
    Languages: languages,
    LearningPaths: learningPaths,
    LoaderScreen: loaderScreen,
    Menu: menu,
    MicroLearning: microLearning,
    Notes: notes,
    Ratings: ratings,
    Reports: reports,
    ReportsCourses: reportsCourses,
    ReportsUsers: reportsUsers,
    Restricted: restricted,
    Search: search,
    User: user,
    VideoLog: videoLog,
    VideoPlayer: videoPlayer,
    Watchlists: watchlists,
    Hubspot: hubspot,
    ChatBotCombo: chatBotCombo,
    Breadcrumb: breadcrumb,
    Quiz: quiz
}

export default new Vuex.Store({
    state,
    mutations,
    actions,
    plugins,
    getters,
    modules
})
